import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import { SalaryListRow, SalaryStatus } from "src/types/salary";
import TableActions from "./table-actions";

const statusLabelKey = (status: SalaryStatus): string => {
  if (status === "paid") return "statusPaid";
  if (status === "partially_paid") return "statusPartiallyPaid";
  if (status === "over_paid") return "statusOverPaid";
  return "statusUnpaid";
};

const statusChipSx = (status: SalaryStatus) => {
  if (status === "paid") {
    return { bgcolor: "#ECFDF3", color: "#067647", fontWeight: 500 };
  }
  if (status === "partially_paid") {
    return { bgcolor: "#FFFAEB", color: "#B54708", fontWeight: 500 };
  }
  if (status === "over_paid") {
    return { bgcolor: "#FEF3F2", color: "#B42318", fontWeight: 500 };
  }
  return { bgcolor: "#F2F4F7", color: "#344054", fontWeight: 500 };
};

const headCellSx = {
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "18px",
  color: "#475467",
  bgcolor: "#F9FAFB",
  whiteSpace: "nowrap",
} as const;

const SalariesTable = ({
  rows,
  total,
  pageNumber,
  setPageNumber,
  refetch,
}: {
  rows: SalaryListRow[];
  total: number;
  pageNumber: number;
  setPageNumber: (v: number) => void;
  refetch: () => void;
}) => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");

  const tableHeaders = [
    generalT("reference"),
    generalT("branch"),
    t("employee"),
    generalT("type"),
    t("basicSalary"),
    t("allowanceTotal"),
    t("additionalPay"),
    t("deduction"),
    t("netPaySalary"),
    generalT("paymentMethod"),
    t("paymentDate"),
    generalT("receivedDate"),
    generalT("createdBy"),
    generalT("createdAt"),
    generalT("status"),
    generalT("actions"),
  ];

  return (
    <>
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table sx={{ width: "100%", minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              {tableHeaders.map((label) => (
                <TableCell key={label} align="center" sx={headCellSx}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell align="center">
                  <Link
                    component={RouterLink}
                    to={`/salary/salaries?reference=${encodeURIComponent(row.reference)}`}
                    underline="always"
                    sx={{ fontSize: "inherit" }}
                  >
                    {row.reference}
                  </Link>
                </TableCell>
                <TableCell align="center">{row.branch?.name ?? "—"}</TableCell>
                <TableCell align="center">{row.employee?.name ?? "—"}</TableCell>
                <TableCell align="center">{t("title")}</TableCell>
                <TableCell align="center">{row.basic_salary?.toFixed(2)}</TableCell>
                <TableCell align="center">
                  {row.allowance_total?.toFixed(2)}
                </TableCell>
                <TableCell align="center">
                  {row.additional_pay?.toFixed(2)}
                </TableCell>
                <TableCell align="center">{row.deduction?.toFixed(2)}</TableCell>
                <TableCell align="center">
                  {(row.net_salary_to_be_paid ?? row.net_pay_salary)?.toFixed(2)}
                </TableCell>
                <TableCell align="center">{row.payment_method?.name ?? generalT("none")}</TableCell>
                <TableCell align="center">{row.payment_date ?? "—"}</TableCell>
                <TableCell align="center">{row.received_date ?? "—"}</TableCell>
                <TableCell align="center">{row.created_by?.name ?? "—"}</TableCell>
                <TableCell align="center" dir="ltr">
                  {row.created_at ?? "—"}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={t(statusLabelKey(row.status))}
                    sx={{
                      ...statusChipSx(row.status),
                      height: 24,
                      "& .MuiChip-label": { px: 1.25, fontSize: 12 },
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TableActions row={row} refetch={refetch} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        sx={{ mt: 2, border: 0, display: "flex", justifyContent: "flex-end" }}
        component="div"
        count={total}
        page={pageNumber}
        rowsPerPage={20}
        onPageChange={(_, newPage) => setPageNumber(newPage)}
        rowsPerPageOptions={[20]}
        showFirstButton
        showLastButton
      />
    </>
  );
};

export default SalariesTable;
